/**
 * Date calculator helpers and an approximate Hijri (Islamic) calendar
 * converter using the tabular/Kuwaiti algorithm.
 *
 * NOTE: Hijri dates here are arithmetical estimates and may differ ±1 day
 * from official moon-sighting based calendars.
 */
import {
  addDays, addMonths, addYears, subDays, subMonths, subYears,
  differenceInCalendarDays, differenceInCalendarWeeks,
  differenceInCalendarMonths, differenceInCalendarYears,
} from "date-fns";
import type { Holiday } from "@/data/bdHolidays";
import { isHoliday } from "@/data/bdHolidays";

/* ---------------- Difference ---------------- */

export interface DateDiff {
  totalDays: number;
  weeks: number;
  months: number;
  years: number;
  hours: number;
  minutes: number;
  // Calendar breakdown: years/months/days
  ymd: { years: number; months: number; days: number };
}

export function dateDifference(start: Date, end: Date, includeEnd = false): DateDiff {
  const adjEnd = includeEnd ? addDays(end, 1) : end;
  const totalDays = Math.abs(differenceInCalendarDays(adjEnd, start));
  const weeks = Math.floor(totalDays / 7);

  // Y/M/D breakdown
  const a = start <= adjEnd ? start : adjEnd;
  const b = start <= adjEnd ? adjEnd : start;
  let years = differenceInCalendarYears(b, a);
  let probe = addYears(a, years);
  if (probe > b) { years--; probe = addYears(a, years); }
  let months = differenceInCalendarMonths(b, probe);
  let probe2 = addMonths(probe, months);
  if (probe2 > b) { months--; probe2 = addMonths(probe, months); }
  const days = differenceInCalendarDays(b, probe2);

  return {
    totalDays,
    weeks,
    months: Math.abs(differenceInCalendarMonths(adjEnd, start)),
    years: Math.abs(differenceInCalendarYears(adjEnd, start)),
    hours: totalDays * 24,
    minutes: totalDays * 24 * 60,
    ymd: { years, months, days },
  };
}

/* ---------------- Add / subtract ---------------- */

export interface AddOptions {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  operation: "add" | "sub";
}

export function shiftDate(start: Date, opts: AddOptions): Date {
  const { years = 0, months = 0, weeks = 0, days = 0, operation } = opts;
  const totalDays = days + weeks * 7;
  if (operation === "add") {
    return addDays(addMonths(addYears(start, years), months), totalDays);
  }
  return subDays(subMonths(subYears(start, years), months), totalDays);
}

/* ---------------- Working days ---------------- */

export type WeekendType = "fri-sat" | "sat-sun";

export interface WorkingDaysResult {
  totalDays: number;
  workingDays: number;
  weekendDays: number;
  holidayDays: number;
  excludedHolidays: Holiday[];
}

function isWeekend(d: Date, type: WeekendType): boolean {
  const dow = d.getDay(); // 0 Sun .. 6 Sat
  if (type === "fri-sat") return dow === 5 || dow === 6;
  return dow === 0 || dow === 6;
}

export function workingDays(
  start: Date,
  end: Date,
  weekend: WeekendType,
  holidays: Holiday[],
  includeEnd = true,
): WorkingDaysResult {
  const a = start <= end ? start : end;
  const b = start <= end ? end : start;
  const last = includeEnd ? addDays(b, 1) : b;

  let total = 0;
  let working = 0;
  let weekendCount = 0;
  let holidayCount = 0;
  const excludedHolidays: Holiday[] = [];
  const seen = new Set<string>();

  for (let d = new Date(a); d < last; d = addDays(d, 1)) {
    total++;
    const we = isWeekend(d, weekend);
    const hol = isHoliday(d, holidays);
    if (we) weekendCount++;
    if (hol && !we) {
      holidayCount++;
      if (!seen.has(hol.date)) {
        excludedHolidays.push(hol);
        seen.add(hol.date);
      }
    }
    if (!we && !hol) working++;
  }

  return {
    totalDays: total,
    workingDays: working,
    weekendDays: weekendCount,
    holidayDays: holidayCount,
    excludedHolidays,
  };
}

/* ---------------- Hijri (tabular / Kuwaiti algorithm) ---------------- */

const HIJRI_MONTHS_EN = [
  "Muharram", "Safar", "Rabi'-al-Awwal", "Rabi'-al-Thani",
  "Jumada-al-Ula", "Jumada-al-Thani", "Rajab", "Sha'ban",
  "Ramadan", "Shawwal", "Dhul-Qi'dah", "Dhul-Hijjah",
];
const HIJRI_MONTHS_BN = [
  "মুহাররম", "সফর", "রবিউল আউয়াল", "রবিউস সানি",
  "জমাদিউল আউয়াল", "জমাদিউস সানি", "রজব", "শাবান",
  "রমজান", "শাওয়াল", "জিলকদ", "জিলহজ",
];

export interface HijriDate {
  day: number;
  month: number; // 1..12
  year: number;
  monthEn: string;
  monthBn: string;
}

/** Convert Gregorian date to Hijri (Kuwaiti / tabular algorithm). */
export function gregorianToHijri(date: Date): HijriDate {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  let jd: number;
  if (year > 1582 || (year === 1582 && month > 10) || (year === 1582 && month === 10 && day > 14)) {
    jd =
      Math.floor((1461 * (year + 4800 + Math.floor((month - 14) / 12))) / 4) +
      Math.floor((367 * (month - 2 - 12 * Math.floor((month - 14) / 12))) / 12) -
      Math.floor((3 * Math.floor((year + 4900 + Math.floor((month - 14) / 12)) / 100)) / 4) +
      day - 32075;
  } else {
    jd =
      367 * year - Math.floor((7 * (year + 5001 + Math.floor((month - 9) / 7))) / 4) +
      Math.floor((275 * month) / 9) + day + 1729777;
  }

  const l1 = jd - 1948440 + 10632;
  const n = Math.floor((l1 - 1) / 10631);
  const l2 = l1 - 10631 * n + 354;
  const j =
    Math.floor((10985 - l2) / 5316) * Math.floor((50 * l2) / 17719) +
    Math.floor(l2 / 5670) * Math.floor((43 * l2) / 15238);
  const l3 =
    l2 - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50) -
    Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29;
  const hMonth = Math.floor((24 * l3) / 709);
  const hDay = l3 - Math.floor((709 * hMonth) / 24);
  const hYear = 30 * n + j - 30;

  const idx = hMonth - 1;
  return {
    day: hDay,
    month: hMonth,
    year: hYear,
    monthEn: HIJRI_MONTHS_EN[idx] ?? "",
    monthBn: HIJRI_MONTHS_BN[idx] ?? "",
  };
}

import { toBengaliNumerals } from "@/lib/bdNumerals";

export function formatHijri(h: HijriDate, lang: "en" | "bn" = "en"): string {
  if (lang === "bn") {
    return `${toBengaliNumerals(h.day)} ${h.monthBn} ${toBengaliNumerals(h.year)}`;
  }
  return `${h.day} ${h.monthEn} ${h.year} AH`;
}

export function differenceInCalendarWeeksAbs(a: Date, b: Date): number {
  return Math.abs(differenceInCalendarWeeks(a, b));
}
